const {
	cleanTopic,
	validateRoute,
	joinRoutes,
	convertRouteToMqttTopic,
	convertMqttTopicToRegex,
} = require('./utils');
const Endpoint = require('./endpoint');

// pre-compiled regex to increase performance
const leadingSlashRegex = /^\//;

/**
 * Utility class for mapping routes to handlers
 * @template E
 */
class MqttRouter {
	/** @param {String} name
	 * @param {Array<import('./types').MqttMiddleware<E>>} middlewares
	 */
	constructor(name = null, middlewares = []) {
		/** @private */
		this.middlewares = Array.isArray(name) ? name : middlewares || [];

		/** @private */
		this.name = typeof name === 'string' ? name : 'MqttRouter';

		/** @private
		 * @type {Array<Endpoint>} */
		this.endpoints = [];

		/** @private
		 * @type {string[]} */
		this._routes_cache = null;

		/** @private
		 * @type {string[]} */
		this._mqtt_topics_cache = null;

		// bind these class methods to itself; they are used in callbacks
		this.onNewMessage = this.onNewMessage.bind(this);
		this.listen = this.listen.bind(this);
	}

	/**
	 * Called to notify this router of a new message
	 * @param {import('./types').MessageHandlerFuncArgs<E>} args Arguments received from parent router
	 */
	onNewMessage(args) {
		const { params, topic: matchingTopic, originalTopic } = args;
		this.endpoints.forEach(
			/** @param {Endpoint} endpoint */
			endpoint => {
				const match = endpoint.routeRegex.exec(matchingTopic);
				if (match) {
					const updatedParams = Object.assign({}, params || {}, match.groups);
					let continuingTopic = matchingTopic;

					if (endpoint.isSubRouter)
						continuingTopic = matchingTopic
							.replace(endpoint.parentRouteRegex, '')
							// remove leading slash if any
							.replace(leadingSlashRegex, '');

					endpoint.handler({
						...args,
						params: updatedParams || {},
						topic: continuingTopic,
						originalTopic: originalTopic || '',
					});
				}
			}
		);
	}

	/**
	 * Listen for messages on a route.
	 * When a new message is received on a topic matching the route, the handler will be called/notified
	 *
	 * Note that this operation is NOT IDEMPOTENT.
	 * Calling it twice with the same arguments will register the handler twice
	 *
	 * @param {string} route
	 * @param {import('./types').MessageHandlerFunc<E>|MqttRouter<E>} handler
	 * @returns {MqttRouter<E>}
	 */
	listen(route, handler) {
		if (this === handler) throw new Error('A router cannot listen for messages on itself');

		let safeRoute = cleanTopic(route);
		validateRoute(safeRoute);
		const isSubRouter = handler instanceof MqttRouter;
		if (isSubRouter && !safeRoute.endsWith('#'))
			throw new Error('A router can only listen on topics with # wildcard');

		let actualHandler = this._buildHandlerFuncFromMiddlewares(handler);
		this.endpoints.push(
			new Endpoint(safeRoute, actualHandler, isSubRouter ? handler : undefined)
		);

		// the routes for this router has changed, so clear the cache.
		// It will be updated on the next call to this.routes
		this._routes_cache = null;
		this._mqtt_topics_cache = null;

		return this;
	}

	/**
	 * @param {string} mqttTopic
	 * @returns {MqttRouter<E>}
	 */
	unlisten(mqttTopic) {
		const safeTopic = cleanTopic(mqttTopic);
		const mqttTopicRegex = convertMqttTopicToRegex(safeTopic);

		this.endpoints = this.endpoints.filter(endpoint => {
			if (endpoint.mqttTopicRegex.test(safeTopic)) {
				if (endpoint.isSubRouter) {
					// unlisten for the wildcard part of the topic that matches this endpoint's router's endpoints
					endpoint.router.unlisten(
						safeTopic
							.replace(endpoint.parentMqttTopicRegex, '')
							.replace(leadingSlashRegex, '')
					);
					// if the sub-router still has some endpoints, retain it. Else remove it
					return endpoint.router.endpoints.length !== 0;
				}
				return false;
			}
			// check if the incoming topic engulfs the topic on which this endpoint is listening
			else if (mqttTopicRegex.test(endpoint.mqttTopic)) {
				if (endpoint.isSubRouter) endpoint.router.unlisten('#');
				return false;
			}

			// this endpoint's route did not match, we maintain it in the list
			return true;
		});

		// the routes for this router has changed, so clear the cache.
		// It will be updated on the next call to this.routes
		this._routes_cache = null;
		this._mqtt_topics_cache = null;
		return this;
	}

	/** @private
	 * @param {MqttRouter<E>|import('./types').MessageHandlerFunc<E>} handler
	 * @returns {import('./types').MessageHandlerFunc<E>}
	 */
	_buildHandlerFuncFromMiddlewares(handler) {
		return this.middlewares.reduceRight(
			(prev, middleware) => middleware(prev),
			handler instanceof MqttRouter ? handler.onNewMessage : handler
		);
	}

	/** Get all the routes that have been registered on this router,
	 *  including all the routes of any sub-routers
	 *
	 * @returns {String[]}
	 */
	get routes() {
		if (this._routes_cache === null) {
			this._routes_cache = [];
			this.endpoints.forEach(endpoint => {
				if (endpoint.isSubRouter) {
					this._routes_cache.push(
						...endpoint.router.routes.map(
							// join the endpoint's topic to the topics from the sub-router
							subRoute => joinRoutes(endpoint.route, subRoute)
						)
					);
				} else {
					this._routes_cache.push(endpoint.route);
				}
			});
		}
		return this._routes_cache;
	}

	/**
	 * Get all the MQTT topics that have been registered on this router,
	 *  including mqttTopics of any sub-routers.
	 *
	 * @returns {String[]}
	 */
	get mqttTopics() {
		if (this._mqtt_topics_cache === null) {
			this._mqtt_topics_cache = this.routes.map(route => convertRouteToMqttTopic(route));
		}
		return this._mqtt_topics_cache;
	}
}

module.exports = MqttRouter;
